import { action, makeAutoObservable, runInAction } from 'mobx'
import { injectable, inject, decorate } from 'inversify'

// evertel
import { Api } from '@evertel/api'
import { DisplayThread, ThreadStore } from '@evertel/stores'
import { SessionService } from '@evertel/session/SessionService'
import { BlueUserStore, FetchUserService } from '@evertel/blue-user'

class ThreadController {
    threadId = 0
    joinRequestsCount = 0

    constructor(
        private api: Api,
        private session: SessionService,
        private threadStore: ThreadStore,
        private userStore: BlueUserStore,
        private fetchUserService: FetchUserService
    ) {
        makeAutoObservable(this, {init: action})
    }

    init = (threadId: number) => {
        if (!threadId) return

        this.threadId = threadId
    }

    fetchThreadDetails = async () => {
        await Promise.all([
            this.fetchThread()
        ])
    }

    fetchThread = async () => {
        const thread = (await this.api.Routes.Thread.getById(this.threadId, {
            include: {
                relation: 'users',
                scope: {
                    fields: ['id', 'firstName', 'lastName']
                }
            }
        })) as DisplayThread

        const userIds = thread.users?.map((u: any) => u.id) ?? []

        runInAction(() => {
            thread.userIds = userIds
            thread.isBotThread = !!thread.meta?.isBotThread
            this.threadStore.update(thread)
        })

        // fetch full user profiles (with departmentsAccess) via FetchUserService
        if (userIds.length) {
            await this.fetchUserService.fetchSpecificUsersFromThread(this.threadId, userIds)
        }

        runInAction(() => {
            thread.name = this.name
            this.threadStore.update(thread)
        })

        // closed threads are still viewable by id, so resolve whether the
        // current user has this thread closed (hidden) if we don't know yet
        if (this.thread?.isClosed === undefined) {
            await this.fetchIsClosed()
        }
    }

    // The API only exposes a user's closed threads through the threads list
    // endpoint with a 'disabled' where flag (see BlueUser/get-threads.js),
    // so query it scoped to this thread id
    private fetchIsClosed = async () => {
        try {
            const closedMatch = await this.api.Routes.BlueUser.getThreads(this.session.currentUserId, {
                where: { disabled: true, id: this.threadId },
                fields: ['id']
            })

            runInAction(() => {
                this.threadStore.update({ id: this.threadId, isClosed: !!closedMatch?.length } as DisplayThread)
            })
        } catch (error: any) {
            console.error(error.message)
        }
    }

    closeThread = async () => {
        await this.api.Routes.Thread.delById(this.threadId)

        runInAction(() => {
            // keep the thread in the store, flagged closed, so it can show
            // up in the drawer's closed conversations list
            this.threadStore.update({ id: this.threadId, isClosed: true } as DisplayThread)
        })
    }

    reopenThread = async () => {
        await this.api.Routes.Thread.postReopen(this.threadId)

        runInAction(() => {
            this.threadStore.update({ id: this.threadId, isClosed: false } as DisplayThread)
        })
    }

    /**
     * @deprecated
     */
    get id(): number {
        console.error('DO NOT USE ThreadController.id')
        return this.threadId
    }

    get thread() {
        return this.threadStore.findById(this.threadId)
    }

    get users() {
        const userIds = (this.threadStore.findById(this.threadId) as any)?.userIds ?? []
        return this.userStore.findByIds(userIds)
    }

    get usersNotMe() {
        return this.users?.filter(u => u.id !== this.session.currentUserId)
    }

    get isJustMe() {
        return this.users?.length === 1 && this.users[0].id === this.session.currentUserId
    }

    get canLeave() {
        return !this.isJustMe && !this.isBotThread && !this.isClosed
    }

    // true when the current user has closed (hidden) this thread
    get isClosed() {
        return !!this.thread?.isClosed
    }

    get name() {
        if (this.isJustMe) {
            return `${this.users[0].firstName} ${this.users[0].lastName}`
        } else if (this.usersNotMe?.length === 1) {
            return `${this.usersNotMe[0].firstName} ${this.usersNotMe[0].lastName}`
        } else {
            const names = this.usersNotMe?.map(u => u.firstName?.charAt(0) + '. ' + u.lastName)
            return names?.join(', ')
        }
    }

    get isBotThread() {
        const meta = this.thread?.meta as any
        return meta?.isBotThread ?? false
    }

    get userInitials(): string {
        const users = this.isJustMe ? this.users : this.usersNotMe
        return users
            ?.map(u => `${u.firstName?.[0] || ''}${u.lastName?.[0] || ''}`.toUpperCase())
            .filter(Boolean)
            .join(', ') || ''
    }
}

let i = 0
decorate(injectable(), ThreadController)
decorate(inject(Api), ThreadController, i++)
decorate(inject(SessionService), ThreadController, i++)
decorate(inject(ThreadStore), ThreadController, i++)
decorate(inject(BlueUserStore), ThreadController, i++)
decorate(inject(FetchUserService), ThreadController, i++)

export { ThreadController }
